import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { useKbs } from "@/features/knowledge-bases/hooks/useKbs";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
}

export function KnowledgeTab({ draft, onChange }: Props) {
  const { t } = useTranslation("agents");
  const [search, setSearch] = useState("");
  const { data: kbs, isLoading } = useKbs();

  /**
   * UI mostra todas as KBs visiveis ao USER atual; o backend valida na
   * hora de salvar se cada KB esta acessivel ao OWNER_TEAM do agente.
   * KBs onde o owner_team eh dono OU eh grantee aparecem; demais a UI
   * mostra mas o save vai falhar com 422 ate o admin der read.
   */
  const filtered = useMemo(() => {
    if (!kbs) return [];
    const q = search.trim().toLowerCase();
    if (!q) return kbs;
    return kbs.filter(
      (kb) =>
        kb.name.toLowerCase().includes(q) ||
        kb.description?.toLowerCase().includes(q),
    );
  }, [kbs, search]);

  function toggle(kbId: string, checked: boolean) {
    const set = new Set(draft.kb_ids);
    if (checked) set.add(kbId);
    else set.delete(kbId);
    onChange({ ...draft, kb_ids: [...set] });
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("knowledge.title")}</h2>
      </div>
      <Input
        placeholder={t("knowledge.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("knowledge.loading")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("knowledge.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((kb) => {
            const checked = draft.kb_ids.includes(kb.id);
            return (
              <li
                key={kb.id}
                className="flex items-start gap-3 rounded-md border p-3"
              >
                <input
                  id={`kb-${kb.id}`}
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => toggle(kb.id, e.target.checked)}
                  className="mt-1"
                />
                <Label
                  htmlFor={`kb-${kb.id}`}
                  className="flex-1 cursor-pointer"
                >
                  <div className="font-medium">{kb.name}</div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {kb.description ?? t("knowledge.noDescription")}
                  </p>
                </Label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
